"use client";

import { useLocale } from "next-intl";
import { useEffect } from "react";
import { resolveInitialCountryCode } from "@/lib/client/browserCountry";
import { COUNTRY_STORAGE_KEY, useCountryStore } from "@/lib/client/stores/useCountryStore";
import { GEO_TO_COUNTRY_CODE, type ICountry } from "@/lib/shared/constants";

interface CountryProviderProps {
  children: React.ReactNode;
}

/**
 * CountryProvider - Hydrates the country store on the client
 *
 * This component initializes the Zustand store after first paint so route
 * rendering does not wait on the DB.
 */
export const CountryProvider = ({ children }: CountryProviderProps) => {
  const locale = useLocale();
  const setCountry = useCountryStore((state) => state.setCountry);
  const setCountries = useCountryStore((state) => state.setCountries);

  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      try {
        const response = await fetch("/api/countries");
        if (!response.ok) return;

        const countries = (await response.json()) as ICountry[];
        if (cancelled || !Array.isArray(countries)) return;

        setCountries(countries);

        const initialCountryCode = resolveInitialCountryCode({
          urlCountry: new URLSearchParams(window.location.search).get("country"),
          savedCountry: localStorage.getItem(COUNTRY_STORAGE_KEY),
          browserLanguages: [...(navigator.languages ?? []), navigator.language],
          countries,
        });
        if (initialCountryCode) {
          setCountry(initialCountryCode);
          return;
        }

        const geoResponse = await fetch(`/${locale}/api/geo`);
        if (!geoResponse.ok) return;

        const { countryCode } = (await geoResponse.json()) as { countryCode?: string };
        if (cancelled || !countryCode) return;

        const ourCode = GEO_TO_COUNTRY_CODE[countryCode] ?? countryCode;
        const matched = countries.find(
          (country) => country.countryCode === ourCode || country.countryCode === countryCode,
        );
        if (matched) {
          setCountry(matched.countryCode);
          return;
        }

        const cnyCountry = countries.find((country) => country.currencyCode === "CNY");
        if (cnyCountry) {
          setCountry(cnyCountry.countryCode);
        }
      } catch {
        if (cancelled) return;
      }
    };

    void loadCountries();

    return () => {
      cancelled = true;
    };
  }, [locale, setCountries, setCountry]);

  return children;
};
